import { Component, computed, EventEmitter, inject, input, OnInit, Output, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BaseComponent } from '@shared-components/base-component';
import { ComponentHeaderComponent } from '@shared-components/component-header/component-header.component';
import { LoginComponent } from '@shared-components/login/login.component';
import { ModalComponent } from '@shared-components/modal/modal.component';
import { PaymentComponent } from '@shared-components/payment/payment.component';
import { ProductSelectorComponent } from '@shared-components/product-selector/product-selector.component';
import { AppHelper } from '@shared-helpers/appHelper';
import { ExpertModel } from '@shared-model/expert/ExpertModel';
import { MemberCallbackModel } from '@shared-model/member/MemberCallbackModel';
import { CallbackStatus } from '@shared-model/phone/CallbackStatus';
import { CallBackStatusModel } from '@shared-model/phone/CallBackStatusModel';
import { CallbackStatusType } from '@shared-model/phone/CallbackStatusType';
import { PhoneNumberModel } from '@shared-model/phone/PhoneNumberModel';
import { ProductModel } from '@shared-model/products/ProductModel';
import { ProductType } from '@shared-model/products/ProductType';
import { CallbackService } from '@shared-services/callback.service';

@Component({
  selector: 'fourd-callback',
  imports: [ReactiveFormsModule, PaymentComponent, ModalComponent, ComponentHeaderComponent, LoginComponent, ProductSelectorComponent],
  templateUrl: './callback.component.html',
  host: {
    '[class]': 'componentClass'
  },
  providers: [...BaseComponent.componentProviders],
})
export class CallbackComponent extends BaseComponent implements OnInit {
  private defaultPollingTime: number = 1500;
  private defaultCloseTimer: number = 5000;

  private callbackService = inject(CallbackService);

  readonly expert = input<ExpertModel>(undefined);
  showPayment: boolean = false;
  showProductSelectorForCallback: boolean = false;

  @Output() close = new EventEmitter<boolean>();
  @Output() cancelChange = new EventEmitter<boolean>();
  @Output() callBackChange = new EventEmitter<boolean>();
  @Output() queueRequest = new EventEmitter<boolean>();
  componentClass: string = "component-container";

  selectedPhoneNumber: FormControl<string>;

  requestCanBeCancelled: boolean = true;
  pollingOnRunning: boolean = false;

  productType = ProductType;
  isCallbackAvailable: Signal<boolean> = computed(() => !this.moduleConfig?.ExpertsSupported || !!this.expert()?.IsOnlineForPhone, { equal: AppHelper.objectsEqual });
  showLogin: Signal<boolean> = computed(() => !this.member()?.IsLoggedIn, { equal: AppHelper.objectsEqual });

  callbackCurrentStatus: CallBackStatusModel = this.callbackService?.getStatusConfig(CallbackStatus.Ready);

  submittingDataIndex: number;
  selectedProduct: ProductModel;
  productListStandardAll: Signal<ProductModel[]> = computed(() => this.memberService?.filterProductsByTypeAndTariff(this.memberService?.memberStandardProducts(), this.moduleConfig?.ProductTypeIdsCallback, this.expert()?.ExpertTariffTypeId), { equal: AppHelper.objectsEqual });
  productListMemberAll: Signal<ProductModel[]> = computed(() => this.memberService?.filterProductsByTypeAndTariff(this.memberService?.memberUserProducts(), this.moduleConfig?.ProductTypeIdsCallback, this.expert()?.ExpertTariffTypeId), { equal: AppHelper.objectsEqual });
  callbackPhoneNumbers: PhoneNumberModel[];
  //productTypes: Signal<number[]> = computed(() => this.productListStandardAll()?.map(p => p.ProductTypeId)?.filter(AppHelper.arrayFilterDistinct), { equal: AppHelper.objectsEqual });
  //bestSellerProductType: Signal<number> = computed(() => this.productListStandardAll()?.find(p => p.IsBestSeller)?.ProductTypeId, { equal: AppHelper.objectsEqual });
  memberObservable$ = toObservable(this.member);

  constructor() {
    super("CallbackComponent");

    this.subscriptionTracker?.add(this.memberLoggedInObservable?.subscribe((memberLoggedIn) => {
      if (memberLoggedIn == true) {
        // Member login state has just changed to logged in
        // Check for in progress callback
        this.checkForInProgressCallback();
      }
    }));
  }

  ngOnInit() {
    this.selectedPhoneNumber = new FormControl<string>("");
    this.setPollingStatus(false);
    this.subscriptionTracker?.add(this.memberObservable$.subscribe((member) => {
      if (member) {
        if (member?.CallbackPhoneNumbers?.length > 0 && !AppHelper.objectsEqual(this.callbackPhoneNumbers, member.CallbackPhoneNumbers)) {
          // Callback numbers are different from what we have, so initialise them
          this.callbackPhoneNumbers = member.CallbackPhoneNumbers;
          // Set selected number to be first in the list
          this.selectedPhoneNumber?.setValue(member.CallbackPhoneNumbers?.find(() => true)?.PhoneNumberDialling);
        }
      }
    }));

    this.showProductSelectorForCallback = false;
  }

  filterProductsByTypeId(productList: ProductModel[], producTypeId: number): ProductModel[] {
    let result: ProductModel[] = [];

    result = productList.filter(pl => pl.ProductTypeId == producTypeId);
    return result;
  }

  updateRequest(request: MemberCallbackModel): void {
    if (request) {
      this.requestCanBeCancelled = request.RequestCanBeCancelled;
      this.callbackCurrentStatus = this.callbackService?.getStatusConfig(request.CallbackStatus);

      switch (request.CallbackStatus) {
        case CallbackStatus.InsufficientCredit: {
          this.showPaymentOptions(this.selectedProduct, this.submittingDataIndex);
          break;
        }
        case CallbackStatus.ExpertBusy: {
          this.constructModalWarning(
            `Sorry, I am now in a ${this.resources?.reading}, you can either wait until I am available or join my queue and I can call you back when I'm free.`,
            () => {
              this.callBackChange.emit(false);
              this.cancelChange.emit(true);
            }
          );
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  request(): void {
    if (this.callbackCurrentStatus.Type != CallbackStatusType.Waiting && !this.pollingOnRunning) {
      this.setPollingStatus(true);
      this.callBackChange.emit(true);
      this.callbackCurrentStatus = this.callbackService?.getStatusConfig(CallbackStatus.Unknown);
      let bundleMinutes: number = this.config()?.SendCallbackMinutesEnabled == true ? this.selectedProduct?.Minutes : -1;
      this.callbackService?.requestNewCallback(this.selectedPhoneNumber?.value, this.expert()?.ExpertId, bundleMinutes, (result) => {
        this.setPollingStatus(false);
        if (result?.MemberCallback) {
          this.updateRequest(result.MemberCallback);
          if (this.callbackCurrentStatus.Type == CallbackStatusType.Waiting) {
            this.getRequest(this.expert()?.ExpertId);
          }
          else {
            this.showProductSelectorForCallback = false;
          }
        }
        else {
          this.showProductSelectorForCallback = false;
          this.callbackCurrentStatus = this.callbackService?.getStatusConfig(CallbackStatus.Error);
          this.callBackChange.emit(false);
        }
      });
    }
  }

  getRequest(expertId: number): void {
    this.showProductSelectorForCallback = false;
    this.setPollingStatus(true);
    setTimeout(() => {
      this.callbackService?.getCallbackStatus(expertId, (result) => {
        if (result?.MemberCallback) {
          this.updateRequest(result.MemberCallback);

          if (this.callbackCurrentStatus.Type == CallbackStatusType.Waiting ||
            this.callbackCurrentStatus.Type == CallbackStatusType.Error) {
            this.getRequest(expertId);
          }
          if (this.callbackCurrentStatus.Type == CallbackStatusType.CompletedAndClose) {
            this.callBackChange.emit(false);
            this.setPollingStatus(false);
            setTimeout(() => {
              this.cancelChange.emit(true);
            }, this.defaultCloseTimer);
          }
          if (this.callbackCurrentStatus.Type == CallbackStatusType.Completed) {
            this.callBackChange.emit(false);
            this.setPollingStatus(false);
            setTimeout(() => {
              this.callbackCurrentStatus = this.callbackService?.getStatusConfig(CallbackStatus.Ready);
            }, this.defaultCloseTimer);
          }
        }
        else {
          this.getRequest(expertId);
        }
      });
    }, this.defaultPollingTime);
  }

  cancelCallbackRequest(event: Event): void {
    this.handleClickEvent(event);
    this.cancelRequest(null, null);
  }

  cancelRequest(requestStatus?: CallBackStatusModel, requestCanBeCancelled?: boolean): void {
    // Set parameters if not provided
    if (requestStatus == null) requestStatus = this.callbackCurrentStatus;
    if (requestCanBeCancelled == null) requestCanBeCancelled = this.requestCanBeCancelled;
    if (requestStatus?.Type != CallbackStatusType.Completed &&
      requestStatus?.Type != CallbackStatusType.CompletedAndClose) {
      if (requestCanBeCancelled) {
        this.callbackService?.cancelCallback((result) => {
          this.callBackChange.emit(false);
          this.cancelChange.emit(true);
        });
      }
    } else {
      this.callBackChange.emit(false);
      this.cancelChange.emit(true);
    }
  }

  closePayment(eventData: boolean): void {
    this.showPayment = false;
    this.submittingDataIndex = -1;
    if (eventData == true) {
      // Successful payment, submit request
      this.showProductSelectorForCallback = false;
      this.request();
    }
    else {
      // Unsuccessful payment, cancel request
      this.cancelRequest(this.callbackCurrentStatus, this.requestCanBeCancelled);
    }
  }

  selectedProductChangedForCallback(eventData: ProductModel): void {
    this.showProductSelectorForCallback = false;
    this.selectedProduct = eventData;
    if (eventData) {
      this.request();
    }
  }

  checkForInProgressCallback(): void {
    // Check most recent callback request
    this.callbackService?.getCallbackStatus(null, (result) => {
      if (result?.MemberCallback) {
        let mostRecentStatus = this.callbackService?.getStatusConfig(result?.MemberCallback?.CallbackStatus);
        if (mostRecentStatus?.Type == CallbackStatusType.Waiting) {
          // There is already a callback in progress
          // Update status data
          this.requestCanBeCancelled = result?.MemberCallback?.RequestCanBeCancelled;
          this.callbackCurrentStatus = mostRecentStatus;
          // Ask customer what to do
          this.constructModalWarningYesNo(
            "You already have a callback request in progress, would you like to cancel it?",
            () => { this.cancelRequest(mostRecentStatus, result?.MemberCallback?.RequestCanBeCancelled); },
            () => {
              this.showProductSelectorForCallback = false;
              // Get most recent status and start polling for updates
              this.getRequest(result?.MemberCallback?.ExpertId);
            }
          );
        }
      }
    });
  }

  showPaymentOptions(product: ProductModel, index: number): void {
    this.showPayment = true;
    //if (!product) {
    //  // TEMP - keep previous functionality for now while testing new product selector
    //  // To be removed
    //  product = this.productListStandard?.find(() => true);
    //}

    //if (product) {
    //  this.submittingDataIndex = index;
    //  this.selectedProduct = this.memberService?.getMemberProductFromStandard(product);
    //  this.showPayment = true;
    //}
    //else {
    //  this.showProductSelectorForPayment = true;
    //}
  }

  getPaymentProducts(): ProductModel[] {
    const product = this.selectedProduct;
    if (product) {
      // Product selected, return with any offers the member has
      return [this.memberService?.getMemberProductFromStandard(product)];
    }
    // No product selected, pass list to payment
    return this.productListMemberAll();
  }

  sendCallbackRequest(event: Event, product: ProductModel, index: number) {
    this.handleClickEvent(event);
    this.selectedProduct = this.memberService?.getMemberProductFromStandard(product);
    this.submittingDataIndex = index;
    this.request();
  };

  chooseProduct(event: Event): void {
    if (this.moduleConfig?.CallbackProductChoiceEnabled == true) {
      this.handleClickEvent(event);
      // Show product options
      this.showProductSelectorForCallback = true;
    }
    else {
      // Send request without product
      this.sendCallbackRequest(event, null, null);
    }
  }

  switchToQueue(event: Event): void {
    this.handleClickEvent(event);
    this.queueRequest.emit(true);
  }

  setPollingStatus(pollingIsRunning: boolean) {
    this.pollingOnRunning = pollingIsRunning;
    if (pollingIsRunning) {
      this.selectedPhoneNumber?.disable();
    }
    else {
      this.selectedPhoneNumber?.enable();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  closeLogin(): void {
    // Close login triggered
    if (this.showLogin()) {
      // Not logged in yet, so trigger close of this component
      this.onClose();
    }
  }
}
